'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { DbFloorNote } from '@/components/DbFloorNote';
import { INVESTOR_COUNT_CLAIM } from '@/lib/approx';

export interface FitResult {
  slug: string;
  label: string;
  /** Share of the database, 0–100. No absolute counts are sent to the client. */
  share: number;
  cities: string[];
  focus: string[];
}

export function VcFitChecker({ results }: { results: FitResult[] }) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState(results[0]?.slug ?? '');
  const r = results.find((x) => x.slug === slug) ?? results[0];

  return (
    <>
      <nav className="text-sm text-gray-500 mb-6">
        <Link href="/" className="hover:underline">{t('pageContent.fitChecker.home')}</Link>
        <span className="mx-2">/</span>
        <span>{t('pageContent.fitChecker.crumb')}</span>
      </nav>
      <h1 className="text-4xl font-bold mb-4">{t('pageContent.fitChecker.title')}</h1>
      <p className="text-lg text-gray-700 mb-8">
        {t('pageContent.fitChecker.intro', { claim: INVESTOR_COUNT_CLAIM })}
      </p>

      {r ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8">
          <label htmlFor="fit-sector" className="block text-sm font-semibold text-gray-900 mb-2">
            {t('pageContent.fitChecker.question')}
          </label>
          <select
            id="fit-sector"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-base"
          >
            {results.map((o) => (
              <option key={o.slug} value={o.slug}>{o.label}</option>
            ))}
          </select>

          <div aria-live="polite" className="mt-8">
            <div className="rounded-xl border p-4 mb-6">
              <div className="text-3xl font-bold">{r.share}%</div>
              <div className="text-sm text-gray-600 mt-1">
                {t('pageContent.fitChecker.shareLabel', { sector: r.label })}
              </div>
            </div>

            {r.cities.length > 0 && (
              <>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">{t('pageContent.fitChecker.cities')}</h3>
                <ul className="flex flex-wrap gap-2">
                  {r.cities.map((c) => (
                    <li key={c} className="rounded-full border px-3 py-1 text-sm">{c}</li>
                  ))}
                </ul>
              </>
            )}
            {r.focus.length > 0 && (
              <>
                <h3 className="mt-6 mb-2 text-sm font-semibold text-gray-900">{t('pageContent.fitChecker.focus')}</h3>
                <ul className="flex flex-wrap gap-2">
                  {r.focus.map((f) => (
                    <li key={f} className="rounded-full border px-3 py-1 text-sm">{f}</li>
                  ))}
                </ul>
              </>
            )}

            <DbFloorNote className="mt-6" />

            <div className="mt-8 flex flex-wrap gap-3">
              <Link href="/free-vc-list" className="rounded-lg bg-black px-5 py-3 text-white font-medium">
                {t('pageContent.fitChecker.ctaList')}
              </Link>
              <Link href="/match-preview" className="rounded-lg border px-5 py-3 font-medium">
                {t('pageContent.fitChecker.ctaMatch')}
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-600">{t('pageContent.fitChecker.unavailable')}</p>
      )}

      <h2 className="text-2xl font-semibold mt-12 mb-3">{t('pageContent.fitChecker.howTitle')}</h2>
      <p className="text-gray-700 mb-4">{t('pageContent.fitChecker.how1')}</p>
      <p className="text-gray-700">
        {t('pageContent.fitChecker.how2a')}{' '}
        <Link href="/free-vc-list" className="text-blue-700 underline">{t('pageContent.fitChecker.how2list')}</Link>
        {t('pageContent.fitChecker.how2b')}{' '}
        <Link href="/match-preview" className="text-blue-700 underline">{t('pageContent.fitChecker.how2match')}</Link>
        .
      </p>
    </>
  );
}
